import { Component, HostBinding, inject, OnDestroy } from '@angular/core';
import { RouterModule } from "@angular/router";

import { DxHttpModule } from "devextreme-angular/http";
import notify from 'devextreme/ui/notify';
import { TranslateService } from '@ngx-translate/core';

import { ScreenService, ThemeService } from './services';
import { AuthService } from './services/auth.service';
import { LanguageService } from './services/language.service';
import { ChatNotificationOverlayComponent } from './shared/components/chat-notification-overlay/chat-notification-overlay.component';
import { ChatNotificationsService } from './shared/services/chat-notifications.service';
import { ExpiryModalComponent } from './shared/components/expiry-modal/expiry-modal.component';
import { FaviconBadgeService } from './shared/services/favicon-badge.service';
import { PushService } from './shared/services/push.service';
import { RouteProgressBarComponent } from './shared/components/route-progress-bar/route-progress-bar.component';
import { CommandPaletteComponent } from './shared/components/command-palette/command-palette.component';
import { CommandPaletteService } from './shared/services/command-palette.service';
import { QuickViewPopoverComponent } from './shared/components/quick-view-popover/quick-view-popover.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    DxHttpModule,
    RouterModule,
    ChatNotificationOverlayComponent,
    RouteProgressBarComponent,
    ExpiryModalComponent,
    CommandPaletteComponent,
    QuickViewPopoverComponent,
  ]
})
export class AppComponent implements OnDestroy {
  @HostBinding('class') get getClass() {
    const classes = Object.keys(this.screen.sizes).filter((cl) => this.screen.sizes[cl]);
    classes.push(this.themeService.currentTheme);
    return classes.join(' ');
  }
  private themeService =  inject(ThemeService);
  private screen = inject(ScreenService);
  /** Inject del service en el root para que arranque ASAP — su constructor
   *  monta el effect que detecta mensajes nuevos y los reglas globales
   *  (titulo, sonido, browser notifs). Sin este inject el service nunca
   *  se instanciaria hasta que algun componente lo use. */
  private chatNotifs = inject(ChatNotificationsService);
  /** Inject eager — su effect escucha `totalNoLeidos` y redibuja el favicon
   *  con un badge rojo cuando hay mensajes sin leer. Sin este inject el
   *  servicio nunca se materializaria. */
  private faviconBadge = inject(FaviconBadgeService);
  /** Inject eager — su constructor sincroniza ngx-translate + DevExtreme locale
   *  con el idioma persistido en localStorage. Sin esto, los textos arrancarian
   *  en default-lang ('es') hasta que el primer componente con `| translate`
   *  fuerce a translate.use(). */
  private language = inject(LanguageService);
  private auth = inject(AuthService);
  private translate = inject(TranslateService);
  private push = inject(PushService);
  /** Inject eager — su constructor registra el listener global ⌘K/Ctrl+K.
   *  Sin esto el atajo no funcionaria hasta que algun componente lo materialice. */
  private commandPalette = inject(CommandPaletteService);

  constructor() {
    this.themeService.setAppTheme();
    this.avisarSiJwtEsViejo();
    // Re-vincula la push subscription al backend si el browser ya tiene permission
    // (caso: user se logueo en una sesion anterior, dio OK al prompt, y vuelve).
    // No molesta al user — `tryAutoSubscribe` es no-op si no hay permission previa.
    setTimeout(() => void this.push.tryAutoSubscribe(), 2000);
  }

  /**
   * Si el usuario tiene una sesión iniciada con un JWT que carece de claims
   * nuevos (típicamente porque ya estaba logueado antes de un deploy que
   * agregó claims), le mostramos un toast pidiendo re-login. Sin re-login,
   * algunas funcionalidades nuevas no van a aparecer (ej. botón Eliminar
   * del chat para usuarios IONICS). El toast es no-bloqueante; el user puede
   * ignorarlo y seguir usando la app con su set de claims viejo.
   *
   * Se ejecuta una sola vez al boot del componente raíz (cada full refresh).
   * Para más detalle, ver `AuthService.esJwtViejo()`.
   */
  private avisarSiJwtEsViejo(): void {
    // Demora corta para no pisar el splash + dar tiempo a que language cargue.
    setTimeout(() => {
      if (!this.auth.esJwtViejo()) return;
      const msg = this.translate.instant('auth.sesionDesactualizada');
      notify({
        message: msg || 'Tu sesión está desactualizada. Cerrá sesión y volvé a entrar para acceder a las nuevas funcionalidades.',
        type: 'warning',
        displayTime: 8000,
        width: 420,
        position: { my: 'top right', at: 'top right', offset: '-20 70' },
      });
    }, 1500);
  }

  ngOnDestroy(): void {
    this.screen.breakpointSubscription.unsubscribe();
  }
}
